// Copyright 2017 Sorama B.V.

import { CaptureDevice, CaptureDeviceImageRotation } from "datatypes/SoramaLocalApi";
import { WebcamDTO, WebcamImageOrientation } from "datatypes/SoramaWebApi";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { MeasurementDeviceSetup } from "services/Hardware/Datatypes/MeasurementDeviceSetup";

export class ManagementHelperFunctions {
    //#region Static Properties
    private static ipRegex = /(\d{1,3}\.){3}\d{1,3}/;

    private static MaxHorizontalMeasurementPoints = 160; // Based on a 1mm resolution 8x8 array scan, this has 160 points horizontally and vertically
    private static MaxVerticalMeasurementPoints = 160; // Based on a 1mm resolution 8x8 array scan, this has 160 points horizontally and vertically

    private static SampleRate = 46875.0;
    private static DmadaqMinimumMemorySize = 1000000000; // 1 GB RAM
    private static Cam1KMinimumMemorySize = 2000000000; // 2 GB RAM Total
    private static Usb2MaximumTransferRate = 15000000;
    private static AbsoluteMaximumMeasurementDuration = 60; // s
    //#endregion

    public static webcamToCaptureDevice(webcam: WebcamDTO, captureDevice: CaptureDevice): CaptureDevice {
        captureDevice.productId = webcam.DeviceId;
        captureDevice.vendorId = webcam.VendorId;
        captureDevice.name = webcam.Name;

        captureDevice.cropToWideScreen = webcam.CropToWideScreen;
        captureDevice.calibration = {
            focalX: webcam.Calibration.FocalX,
            focalY: webcam.Calibration.FocalY,
            principalX: webcam.Calibration.PrincipalX,
            principalY: webcam.Calibration.PrincipalY,
        };
        captureDevice.sensorOffsetCorrection = webcam.SensorOffsetCorrection
            ? {
                  x: webcam.SensorOffsetCorrection.X,
                  y: webcam.SensorOffsetCorrection.Y,
              }
            : null;

        captureDevice.position = {
            X: webcam.Position.X / 1000,
            Y: webcam.Position.Y / 1000,
            Z: webcam.Position.Z / 1000,
        };

        switch (webcam.WebcamImageOrientation) {
            case WebcamImageOrientation.Rotate90:
                captureDevice.imageRotation = CaptureDeviceImageRotation.Rotate90;
                break;

            case WebcamImageOrientation.Rotate180:
                captureDevice.imageRotation = CaptureDeviceImageRotation.Rotate180;
                break;

            case WebcamImageOrientation.Rotate270:
                captureDevice.imageRotation = CaptureDeviceImageRotation.Rotate270;
                break;

            default:
                captureDevice.imageRotation = CaptureDeviceImageRotation.Default;
                break;
        }

        return captureDevice;
    }

    public static filterCaptureDevicesForMeasurementDevice(measurementDevice: MeasurementDevice): CaptureDevice[] {
        // Check if the webcam and front and both contain an ip address, if so, remove any webcam with a different ip
        const frontEndIps = ManagementHelperFunctions.getDomainsForMeasurementDevice(measurementDevice);

        // Filter out webcams with an ip that is not in the set of ips of the connectors
        return measurementDevice.captureDevices.filter((x) => ManagementHelperFunctions.isSameDomain(x, frontEndIps));
    }

    public static filterLocalConnectedMeasurementDevices(measurementDevices: MeasurementDevice[]): MeasurementDevice[] {
        const devices: MeasurementDevice[] = [];

        for (const measuremenDevice of measurementDevices) {
            // Check if local network
            const localDevice = ManagementHelperFunctions.getDomainsForMeasurementDevice(measuremenDevice);
            if (localDevice.length > 0 && localDevice[0].indexOf("169.254.") == 0) devices.push(measuremenDevice);
            // check if usb
            else if (measuremenDevice.frontEnd.frontEndConnectors[0].connectionInfo.indexOf("USB") != -1)
                devices.push(measuremenDevice);
        }

        return devices;
    }

    public static filterConnectedCaptureDevicesForMeasurementDevice(
        measurementDevice: MeasurementDevice,
        captureDevices: CaptureDevice[]
    ): CaptureDevice[] {
        const frontEndIps = ManagementHelperFunctions.getDomainsForMeasurementDevice(measurementDevice);

        return measurementDevice.captureDevices.filter((x) => {
            const matchingCaptureDevices = captureDevices.filter(
                (y) => y.productId === x.productId && y.vendorId === x.vendorId
            );
            for (const dev of matchingCaptureDevices) {
                if (ManagementHelperFunctions.isSameDomain(dev, frontEndIps)) return true;
            }

            return false;
        });
    }

    public static getDomainsForMeasurementDevice(measurementDevice: MeasurementDevice) {
        return measurementDevice.frontEnd.frontEndConnectors
            .map((x) => {
                const match = x.connectionInfo.match(ManagementHelperFunctions.ipRegex);
                if (match) return match[0];
                return null;
            })
            .filter((x) => x !== null);
    }

    public static isSameDomain(captureDevice: CaptureDevice, domains: string[]): boolean {
        const match = captureDevice.uri ? captureDevice.uri.match(ManagementHelperFunctions.ipRegex) : undefined;
        if (match) {
            if (domains.some((ip) => ip === match[0])) return true;
        } else if (!captureDevice.isNetworkDevice) {
            // Always accept connected local devices
            return true;
        }

        return false;
    }

    public static getMaximumMeasurementDuration(activeDeviceSetup: MeasurementDeviceSetup): number {
        if (!activeDeviceSetup) return Number.POSITIVE_INFINITY;

        let minMaxMeasurementDuration = Number.POSITIVE_INFINITY;

        for (const activeDevice of activeDeviceSetup) {
            const nrOfMics = activeDevice.channelSelection.selectedCount;
            const bytesPerSample = 4;
            const sizePerSecond = Math.max(
                nrOfMics * bytesPerSample * ManagementHelperFunctions.SampleRate -
                    ManagementHelperFunctions.Usb2MaximumTransferRate,
                0
            );

            if (Math.abs(sizePerSecond) >= Number.MIN_VALUE) {
                const isDmadaqCam = activeDevice.measurementDevice.frontEnd.frontEndConnectors.some(
                    (x) => x.connectionInfo.toLowerCase().indexOf("usb") >= 0
                );
                const maxMeasurementDuration = Math.floor(
                    (isDmadaqCam
                        ? ManagementHelperFunctions.DmadaqMinimumMemorySize
                        : ManagementHelperFunctions.Cam1KMinimumMemorySize) / sizePerSecond
                );

                if (maxMeasurementDuration < minMaxMeasurementDuration)
                    minMaxMeasurementDuration = maxMeasurementDuration;
            }
        }

        return Math.min(minMaxMeasurementDuration, ManagementHelperFunctions.AbsoluteMaximumMeasurementDuration);
    }
}
